import React, { useCallback, useEffect, useState } from 'react';
import { MdCheck, MdClose, MdRefresh } from 'react-icons/md';
import attendanceAPI from '../../api/attendanceAPI';
import { useAttendanceAuth } from '../../context/AttendanceAuthContext';
import {
  CAMERA_PENDING_CHANGED_EVENT,
  formatBookingDay,
  formatBookingRange,
  formatBookingTime,
  getCameraStateStyle,
} from '../../utils/cameraBookingUtils';

const TABS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All' },
];

/**
 * Media inbox: review faculty camera requests, approve or reject them.
 * Each pending row is checked against approved bookings before Approve is enabled.
 */
function CameraMediaInbox() {
  const { getToken } = useAttendanceAuth();

  const [tab, setTab] = useState('pending');
  const [bookings, setBookings] = useState([]);
  const [availabilityById, setAvailabilityById] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const loadBookings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = tab === 'all' ? {} : { state: tab };
      const token = getToken();
      const result = await attendanceAPI.listCameraBookings(params, token);
      const rows = result.bookings || [];
      setBookings(rows);

      // Check every pending row against approved bookings, so Approve can be disabled up front.
      const pendingRows = rows.filter((row) => row.state === 'pending');
      const checks = await Promise.all(
        pendingRows.map((row) =>
          attendanceAPI
            .checkCameraAvailability({ start: row.start, end: row.end }, token)
            .then((availability) => [row.id, availability])
            .catch(() => [row.id, null])
        )
      );
      setAvailabilityById(Object.fromEntries(checks));
    } catch (err) {
      setError(err.message || 'Could not load requests.');
    } finally {
      setLoading(false);
    }
    // getToken is stable for the session; only the tab should trigger a reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    setRejectingId(null);
    setRejectReason('');
    loadBookings();
  }, [loadBookings]);

  const notifyPendingChanged = () => {
    window.dispatchEvent(new Event(CAMERA_PENDING_CHANGED_EVENT));
  };

  const handleApprove = async (booking) => {
    setBusyId(booking.id);
    setError('');
    try {
      await attendanceAPI.approveCameraBooking(booking.id, getToken());
      notifyPendingChanged();
      await loadBookings();
    } catch (err) {
      setError(err.message || 'Could not approve this request.');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (booking) => {
    const reason = rejectReason.trim();
    if (!reason) {
      setError('Please enter a reason for rejecting this request.');
      return;
    }
    setBusyId(booking.id);
    setError('');
    try {
      await attendanceAPI.rejectCameraBooking(booking.id, reason, getToken());
      setRejectingId(null);
      setRejectReason('');
      notifyPendingChanged();
      await loadBookings();
    } catch (err) {
      setError(err.message || 'Could not reject this request.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Camera Media Inbox</h2>
          <p className="text-sm text-gray-500">Approve or reject camera requests. Approved bookings cannot overlap.</p>
        </div>
        <button
          type="button"
          onClick={loadBookings}
          className="flex items-center gap-1 px-3 py-2 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 self-start"
        >
          <MdRefresh /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((item) => (
          <button
            key={item.value}
            type="button"
            onClick={() => setTab(item.value)}
            className={`px-3 py-1.5 text-sm rounded-full border ${
              tab === item.value
                ? 'bg-green-600 border-green-600 text-white'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
      )}

      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Faculty</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Requester</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Purpose</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Location</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">From</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">To</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Status</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-gray-500">Loading…</td>
              </tr>
            )}
            {!loading && bookings.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-gray-500">No requests here.</td>
              </tr>
            )}
            {!loading &&
              bookings.map((booking) => {
                const style = getCameraStateStyle(booking.state);
                const isPending = booking.state === 'pending';
                const availability = availabilityById[booking.id];
                const conflict = isPending && availability?.available === false;
                const isRejecting = rejectingId === booking.id;
                const busy = busyId === booking.id;

                return (
                  <React.Fragment key={booking.id}>
                    <tr className="hover:bg-gray-50 align-top">
                      <td className="px-4 py-3 text-gray-700">{booking.faculty?.name || 'Media reservation'}</td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                        <div>{booking.requested_by || '—'}</div>
                        {booking.entered_by && (
                          <div className="text-xs text-gray-500">Entered by {booking.entered_by}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-800">
                        <div>{booking.purpose}</div>
                        {booking.reference && <div className="text-xs text-gray-500">{booking.reference}</div>}
                        {booking.notes && <div className="text-xs text-gray-500">{booking.notes}</div>}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{booking.location || '—'}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                        {formatBookingDay(booking.start)} {formatBookingTime(booking.start)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                        {formatBookingDay(booking.end)} {formatBookingTime(booking.end)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded ${style.badge}`}>
                          {style.label}
                        </span>
                        {booking.state === 'rejected' && booking.reject_reason && (
                          <div className="mt-1 text-xs text-red-700">{booking.reject_reason}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {isPending ? (
                          <div className="flex flex-col items-end gap-2">
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => handleApprove(booking)}
                                disabled={busy || conflict || !availability}
                                title={conflict ? 'Overlaps an approved booking' : undefined}
                                className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <MdCheck /> Approve
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setRejectingId(isRejecting ? null : booking.id);
                                  setRejectReason('');
                                }}
                                disabled={busy}
                                className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-md border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
                              >
                                <MdClose /> Reject
                              </button>
                            </div>
                            {conflict && (
                              <div className="text-xs text-red-700 text-right">
                                Overlaps approved booking:{' '}
                                {(availability.conflicts || [])
                                  .map((item) => formatBookingRange(item.start, item.end))
                                  .join(', ')}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>

                    {isPending && isRejecting && (
                      <tr className="bg-red-50">
                        <td colSpan={8} className="px-4 py-3">
                          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                            <input
                              type="text"
                              value={rejectReason}
                              onChange={(event) => setRejectReason(event.target.value)}
                              placeholder="Reason for rejecting (shown to the faculty admin)"
                              maxLength={500}
                              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                            />
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => handleReject(booking)}
                                disabled={busy || !rejectReason.trim()}
                                className="px-3 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                              >
                                {busy ? 'Rejecting…' : 'Confirm reject'}
                              </button>
                              <button
                                type="button"
                                onClick={() => setRejectingId(null)}
                                className="px-3 py-2 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default CameraMediaInbox;
